import React, { useState, useEffect } from 'react'
import { Modal, Button, Icon, Input, Select, Tooltip, message } from 'antd'
import { useWardrobe } from '../../../../hooks/useWardrobe'
import { OutfitGrid, OutfitDetailView, useOutfitLibrary } from './shared'
import CreateOutfitModal from './CreateOutfitModal'
import './WardrobeLibraryPickerModal.scss'

const { Option } = Select

/**
 * Modal for browsing and selecting outfits from the library
 *
 * @param {Object} props
 * @param {Boolean} props.visible - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSelect - Selection handler (imageUrl, tag) => void
 * @param {String} props.targetPlaceholder - Placeholder being assigned to
 * @param {Object} props.initialOutfit - Initial outfit to show in view mode
 * @param {String} props.mode - 'view' or 'select'
 * @param {Object} props.formData - Form data containing outfits
 * @param {Function} props.setFormData - Function to update form data
 */
const WardrobeLibraryPickerModal = ({
  visible,
  onClose,
  onSelect,
  targetPlaceholder,
  initialOutfit = null,
  mode = 'select',
  formData = {},
  setFormData = () => {},
  videoId
}) => {
  const {
    createOutfit,
    isCreatingOutfit: isCreating,
    creationProgress: progress,
    stopPolling
  } = useWardrobe({ videoId })

  const {
    outfits: libraryOutfits,
    isLoading: isLibraryLoading,
    fetchIfNeeded,
    refresh,
    pagination
  } = useOutfitLibrary({ formData, setFormData, videoId })

  const [selectedOutfit, setSelectedOutfit] = useState(null)
  const [activeView, setActiveView] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({ style: '', season: '' })
  const [activeMode, setActiveMode] = useState('list')
  const [showMoreOptions, setShowMoreOptions] = useState(false)
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)

  useEffect(() => {
    if (visible) {
      // Use the centralized hook that checks formData
      fetchIfNeeded()

      if (mode === 'view' && initialOutfit) {
        setSelectedOutfit(initialOutfit)
        // Let OutfitDetailView handle the initial view
      } else {
        setSelectedOutfit(null)
        setActiveView(null)
      }
    }
    return () => stopPolling()
  }, [visible])

  const assignmentTarget = targetPlaceholder
    ? targetPlaceholder.replace(/^_{2}|_{2}$/g, '')
    : 'selected shot'
  const hasAnyFilter = !!filters.style || !!filters.season
  const activeOptionCount = Object.values(filters).filter(Boolean).length

  const fetchWithCurrentMode = async ({
    mode: nextMode = activeMode,
    page = 1,
    nextFilters = filters,
    nextQuery = searchQuery
  } = {}) => {
    const trimmedQuery = (nextQuery || '').trim()

    if (nextMode === 'search' && trimmedQuery) {
      setActiveMode('search')
      return refresh({
        action: 'search',
        q: trimmedQuery,
        page
      })
    }

    const shouldFilter =
      nextMode === 'filter' && (!!nextFilters.style || !!nextFilters.season)

    if (shouldFilter) {
      setActiveMode('filter')
      return refresh({
        action: 'filter',
        style: nextFilters.style || undefined,
        season: nextFilters.season || undefined,
        page
      })
    }

    setActiveMode('list')
    return refresh({
      action: 'list',
      page
    })
  }

  const handleOutfitClick = outfit => {
    setSelectedOutfit(outfit)
    setActiveView(null) // Let OutfitDetailView set the initial view
  }

  const handleApply = (view, outfit) => {
    if (outfit && view) {
      onSelect && onSelect(view.url, outfit.tag || outfit.id)
      message.success(`Selected ${view.label} view of ${outfit.name}`)
      onClose()
    }
  }

  const handleBack = () => {
    setSelectedOutfit(null)
    setActiveView(null)
    // Only close if this is a pure view-only modal (no selection capability)
    // If onSelect exists, it means we came from Selector Modal and should show library
    if (mode === 'view' && initialOutfit && !onSelect) {
      onClose()
    }
  }

  const handleFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value || '' }
    setFilters(newFilters)
    const shouldFilter = !!newFilters.style || !!newFilters.season
    fetchWithCurrentMode({
      mode: shouldFilter ? 'filter' : 'list',
      page: 1,
      nextFilters: newFilters
    })
  }

  const handleSearch = () => {
    const trimmed = searchQuery.trim()

    if (trimmed) {
      fetchWithCurrentMode({
        mode: 'search',
        page: 1,
        nextQuery: trimmed
      })
      return
    }

    fetchWithCurrentMode({
      mode: hasAnyFilter ? 'filter' : 'list',
      page: 1
    })
  }

  const renderLibraryGrid = () => (
    <div className="picker-container">
      <div className="picker-header">
        <div className="picker-heading">
          <h2 className="picker-title">Browse Wardrobe</h2>
          <p>Choose an outfit reference for this shot.</p>
        </div>
        <div className="picker-header-actions">
          <span className="assignment-pill">
            Assigning to: {assignmentTarget}
          </span>
          <Button
            type="link"
            className="close-button"
            aria-label="Close wardrobe picker"
            onClick={onClose}
          >
            <Icon type="close" />
          </Button>
        </div>
      </div>

      <div className="picker-toolbar">
        <div className="toolbar-primary-row">
          <div className="search-section">
            <Input.Group compact className="search-control">
              <Input
                aria-label="Search wardrobe outfits"
                placeholder="Search outfits by name, tag, or description..."
                onChange={e => setSearchQuery(e.target.value)}
                onPressEnter={handleSearch}
                value={searchQuery}
              />
              <Button type="primary" onClick={handleSearch}>
                <Icon type="search" /> Search
              </Button>
            </Input.Group>
          </div>

          <Button
            className={`more-options-button${
              showMoreOptions ? ' more-options-button--open' : ''
            }`}
            aria-expanded={showMoreOptions}
            aria-controls="wardrobe-picker-more-options"
            onClick={() => setShowMoreOptions(current => !current)}
          >
            <Icon type="filter" />
            <span>More options</span>
            {activeOptionCount > 0 && (
              <span className="active-options-count">{activeOptionCount}</span>
            )}
            <Icon type={showMoreOptions ? 'up' : 'down'} />
          </Button>

          <div className="toolbar-actions">
            <Tooltip title="Refresh wardrobe">
              <Button
                className="refresh-button"
                icon="reload"
                aria-label="Refresh wardrobe"
                onClick={() =>
                  fetchWithCurrentMode({ mode: activeMode, page: 1 })
                }
                loading={isLibraryLoading}
              />
            </Tooltip>
            <Button
              type="primary"
              icon="plus"
              className="add-outfit-button"
              onClick={() => {
                setIsCreateModalVisible(true)
              }}
            >
              Add Outfit
            </Button>
          </div>
        </div>

        {showMoreOptions && (
          <div id="wardrobe-picker-more-options" className="filter-section">
            <div className="advanced-options-group">
              <div className="advanced-options-title">Filter outfits</div>
              <div className="advanced-options-fields">
                <div className="filter-field">
                  <label htmlFor="wardrobe-picker-style">Style</label>
                  <Select
                    id="wardrobe-picker-style"
                    placeholder="All styles"
                    value={filters.style || undefined}
                    onChange={val => handleFilter('style', val)}
                    allowClear
                    className="filter-select"
                  >
                    <Option value="formal">Formal</Option>
                    <Option value="casual">Casual</Option>
                    <Option value="sporty">Sporty</Option>
                    <Option value="business">Business</Option>
                  </Select>
                </div>

                <div className="filter-field">
                  <label htmlFor="wardrobe-picker-season">Season</label>
                  <Select
                    id="wardrobe-picker-season"
                    placeholder="All seasons"
                    value={filters.season || undefined}
                    onChange={val => handleFilter('season', val)}
                    allowClear
                    className="filter-select"
                  >
                    <Option value="summer">Summer</Option>
                    <Option value="winter">Winter</Option>
                    <Option value="spring">Spring</Option>
                    <Option value="fall">Fall</Option>
                    <Option value="all-season">All Season</Option>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="picker-content">
        <OutfitGrid
          outfits={libraryOutfits}
          onItemClick={handleOutfitClick}
          isLoading={isLibraryLoading}
          emptyMessage="No outfits found in your library."
          pagination={pagination}
          onPageChange={page =>
            fetchWithCurrentMode({ mode: activeMode, page })
          }
          clampDescriptions
        />
      </div>
    </div>
  )

  const renderDetailView = () => (
    <OutfitDetailView
      outfit={selectedOutfit}
      activeView={activeView}
      onViewChange={setActiveView}
      onApply={onSelect ? handleApply : null}
      onBack={handleBack}
      onClose={onClose}
      mode={mode}
    />
  )

  return (
    <>
      <Modal
        maskClosable={false}
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={selectedOutfit ? 1000 : 920}
        className="wardrobe-library-picker-modal-v2"
        title={null}
        closable={false}
        centered
        destroyOnClose
      >
        {selectedOutfit ? renderDetailView() : renderLibraryGrid()}
      </Modal>

      <CreateOutfitModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
        onSuccess={outfitData => {
          fetchWithCurrentMode({ mode: activeMode, page: 1 })
          if (outfitData && mode === 'select') {
            handleOutfitClick(outfitData)
          }
        }}
        createOutfit={createOutfit}
        isCreating={isCreating}
        creationProgress={progress}
      />
    </>
  )
}

export default WardrobeLibraryPickerModal
